// Availability lookups go through the `availability-cache` edge function, which
// keeps a short-lived (15s) server-side cache in front of the
// get_blocked_slots_v2 / get_booked_slots RPCs. Active browsing (month calendar,
// polling, focus refetches) then collapses into far fewer database round-trips.
//
// If the cache function is unreachable we fall back to calling the RPC directly
// so availability never breaks because of a caching layer.
import { supabase } from "@/integrations/supabase/client";

export type AvailabilitySlot = { start_at: string; end_at: string; source?: string; reason?: string };

export async function fetchAvailability(opts: {
  studioId: string;
  from: string | Date;
  to: string | Date;
  /** true → get_blocked_slots_v2 (includes lock/block source), false → get_booked_slots */
  detailed?: boolean;
}): Promise<AvailabilitySlot[]> {
  const from = typeof opts.from === "string" ? opts.from : opts.from.toISOString();
  const to = typeof opts.to === "string" ? opts.to : opts.to.toISOString();
  const detailed = opts.detailed ?? false;
  if (!opts.studioId) return [];

  try {
    const { data, error } = await supabase.functions.invoke("availability-cache", {
      body: { studio_id: opts.studioId, from, to, detailed },
    });
    if (error) throw error;
    if (data?.slots) return data.slots as AvailabilitySlot[];
    throw new Error(data?.error || "no slots in response");
  } catch {
    const rpc = detailed ? "get_blocked_slots_v2" : "get_booked_slots";
    const { data } = await supabase.rpc(rpc as any, { _studio_id: opts.studioId, _from: from, _to: to });
    return ((data ?? []) as any[]).map((r) => ({
      start_at: r.start_at,
      end_at: r.end_at,
      source: r.source,
      reason: r.reason ?? undefined,
    }));
  }
}
